import React, { useEffect, useState } from 'react';
import { Card } from 'primereact/card';
import { Chart as ChartJS } from 'chart.js/auto';
import { Chart } from 'primereact/chart';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import 'primeflex/primeflex.css';
import {
  GenericMapAgreement,
  GenericMapAgreementLine,
  GenericMapPlace,
  GenericMapRouteLine,
  GeoJsonObject,
  HighlightedMarkers,
  ISmartMapFilterType,
} from '../../../models/GenericMapModels.model';
import { IRoute } from '../../../models';
import { locale } from '../../../common/localization/localizationService';

ChartJS.register(ChartDataLabels);

export const chartCategories = {
  agreements: 'agreements',
  places: 'places',
  bins: 'bins',
  routestops: 'routestops',
};

export interface SmartMapDashboardProps {
  routes: IRoute[];
  agreements: GenericMapAgreement[];
  places: GenericMapPlace[];
  bins: GenericMapAgreementLine[];
  routeLines: GenericMapRouteLine[];
  genericMapActions: any;
  highlightedMarkers: HighlightedMarkers;
  selectedRoutes: GeoJsonObject;
  filterType: ISmartMapFilterType;
}

const SmartMapDashboard: React.FC<SmartMapDashboardProps> = ({
  routes,
  agreements,
  places,
  bins,
  routeLines,
  genericMapActions,
  highlightedMarkers,
  selectedRoutes,
  filterType,
}) => {
  const [activeCategory, setActiveCategory] = useState<string>('');

  useEffect(() => {
    return () => {
      genericMapActions.setHighlightedMarker(null, null);
    };
  }, []);

  useEffect(() => {
    genericMapActions.setHighlightedMarker(null, null);
  }, [activeCategory]);

  const getSelectedRouteIds = (): string[] => {
    const selectedRouteIds: string[] = [];
    selectedRoutes.features?.forEach((route) => {
      const routeId = route.properties.routeId;
      if (!selectedRouteIds.includes(routeId)) {
        selectedRouteIds.push(routeId);
      }
    });
    return selectedRouteIds;
  };

  const selectedRouteIds = getSelectedRouteIds();

  const filterStats = (
    stats:
      | GenericMapAgreement[]
      | GenericMapPlace[]
      | GenericMapAgreementLine[]
      | GenericMapRouteLine[]
  ) => {
    return stats.filter(
      (
        stat:
          | GenericMapAgreement
          | GenericMapPlace
          | GenericMapAgreementLine
          | GenericMapRouteLine
      ) => stat.routeIds.some((routeId) => selectedRouteIds.includes(routeId))
    );
  };

  const filteredBins =
    filterType === ISmartMapFilterType.ROUTE ? filterStats(bins) : bins;

  const filteredAgreements =
    filterType === ISmartMapFilterType.ROUTE
      ? filterStats(agreements)
      : agreements;

  const filteredPlaces =
    filterType === ISmartMapFilterType.ROUTE ? filterStats(places) : places;

  const filteredRoutlines =
    filterType === ISmartMapFilterType.ROUTE
      ? filterStats(routeLines)
      : routeLines;

  const stats = {
    [chartCategories.agreements]: filteredAgreements?.length || 0,
    [chartCategories.places]: filteredPlaces?.length || 0,
    [chartCategories.bins]: filteredBins?.length || 0,
    [chartCategories.routestops]: filteredRoutlines?.length || 0,
  };

  const getBinStats = () => {
    const groupedByUnitName = filteredBins.reduce(
      (acc: { [key: string]: number }, bin) => {
        const unitName = bin.unitName || locale.smartMap.dashboard._unknown;
        if (!acc[unitName]) {
          acc[unitName] = 0;
        }
        acc[unitName]++;
        return acc;
      },
      {}
    );
    return Object.entries(groupedByUnitName).map(([unitName, count]) => ({
      unitName,
      count,
    }));
  };

  const getPlacesStats = () => {
    const groupedByPlaceType = filteredPlaces.reduce(
      (acc: { [key: string]: number }, place) => {
        const placeType = place.placeType || locale.smartMap.dashboard._unknown;
        if (!acc[placeType]) {
          acc[placeType] = 0;
        }
        acc[placeType]++;
        return acc;
      },
      {}
    );
    return Object.entries(groupedByPlaceType).map(([placeType, count]) => ({
      placeType,
      count,
    }));
  };

  const getAgreementStats = () => {
    const groupedByAgreementType = filteredAgreements.reduce(
      (acc: { [key: string]: number }, agreement) => {
        const agreementType =
          agreement.agreementType || locale.smartMap.dashboard._unknown;
        if (!acc[agreementType]) {
          acc[agreementType] = 0;
        }
        acc[agreementType]++;
        return acc;
      },
      {}
    );
    return Object.entries(groupedByAgreementType).map(
      ([agreementType, count]) => ({
        agreementType,
        count,
      })
    );
  };

  const getRouteStats = () => {
    const groupedByRoute = filteredRoutlines.reduce(
      (acc: { [key: string]: number }, route) => {
        const routeId = route.route + '|' + route.paSystem;
        if (!acc[routeId]) {
          acc[routeId] = 0;
        }
        acc[routeId]++;
        return acc;
      },
      {}
    );

    return Object.entries(groupedByRoute).map(([routeId, count]) => {
      const [routeKey, paSystemKey] = routeId.split('|');
      const matchedRoute = routes.find(
        (r: IRoute) => r.routeNumbder === routeKey && r.paSystem === paSystemKey
      );

      return {
        routeId,
        count,
        routeName: matchedRoute
          ? matchedRoute.name
          : locale.smartMap.dashboard._unknown,
      };
    });
  };

  const getChartData = () => {
    if (!activeCategory) return null;

    const binStats = getBinStats();
    const placeStats = getPlacesStats();
    const routeStats = getRouteStats();
    const agreementStats = getAgreementStats();

    switch (activeCategory) {
      case chartCategories.bins:
        return {
          labels: binStats.map((stat) => stat.unitName),
          datasets: [
            {
              label: locale.smartMap.grid._type,
              data: binStats.map((stat) => stat.count),
              backgroundColor: binStats.map((stat) =>
                highlightedMarkers?.value === stat.unitName
                  ? 'black'
                  : '#66b2ff'
              ),
            },
          ],
        };
      case chartCategories.places:
        return {
          labels: placeStats.map((stat) => stat.placeType),
          datasets: [
            {
              label: locale.smartMap.dashboard._placeTypes,
              data: placeStats.map((stat) => stat.count),
            },
          ],
        };
      case chartCategories.routestops:
        return {
          labels: routeStats.map((stat) => stat.routeName),
          datasets: [
            {
              label: locale.smartMap.dashboard._routes,
              data: routeStats.map((stat) => stat.count),
            },
          ],
        };
      case chartCategories.agreements:
        return {
          labels: agreementStats.map((stat) => stat.agreementType),
          datasets: [
            {
              label: locale.smartMap.dashboard._agreements,
              data: agreementStats.map((stat) => stat.count),
            },
          ],
        };
      default:
        return null;
    }
  };

  const chartData = getChartData();

  const chartOptions = {
    indexAxis: 'y',
    maintainAspectRatio: false,
    animation: false,
    onClick: (event: any, elements: any[], chart: any) => {
      if (elements.length > 0) {
        const elementIndex = elements[0].index;

        const label = chart.data.labels[elementIndex];

        if (highlightedMarkers.value === label) {
          genericMapActions.setHighlightedMarker(null, null);
        } else {
          genericMapActions.setHighlightedMarker(activeCategory, label);
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        grace: '10%',
      },
    },
    plugins: {
      tooltip: {
        borderWidth: 1,
        titleFont: { weight: 'bold', size: 14 },
        bodyFont: { size: 12 },
        callbacks: {
          title: (tooltipItems: any) => {
            return `${tooltipItems[0].label}`;
          },
          label: (tooltipItem: any) => {
            return `${locale.smartMap.dashboard._count}: ${tooltipItem.formattedValue}`;
          },
        },
      },
      datalabels: {
        anchor: 'end',
        align: 'end',
        color: '#333',
        formatter: (value: number) => value,
      },
    },
  };

  return (
    <div className="dashboard-summary">
      <div className="smart-map-stat-wrapper p-grid">
        {Object.entries(stats).map(([key, count]) => {
          let title;
          switch (key) {
            case chartCategories.agreements:
              title = locale.smartMap.dashboard._agreements;
              break;
            case chartCategories.places:
              title = locale.smartMap.dashboard._places;
              break;
            case chartCategories.bins:
              title = locale.smartMap.dashboard._bins;
              break;
            case chartCategories.routestops:
              title = locale.smartMap.dashboard._routestops;
              break;
            default:
              title = locale.smartMap.dashboard._unknown;
              break;
          }

          return (
            <div className="card p-col-3" key={key}>
              <Card
                title={title}
                subTitle={locale.smartMap.dashboard._total}
                className={`p-shadow-3 clickable-card ${
                  activeCategory === key ? 'active-card p-highlight' : ''
                }`}
                onClick={() =>
                  setActiveCategory(key === activeCategory ? '' : key)
                }
              >
                <h2 className="no-margin">{count}</h2>
              </Card>
            </div>
          );
        })}
      </div>

      {chartData && (
        <div className="chart-container">
          <Chart
            type="bar"
            data={chartData}
            options={chartOptions}
            className="chart"
          />
        </div>
      )}
    </div>
  );
};

export default SmartMapDashboard;
